/**
 * HTTP impl of :type:`EditsService`(R361;契約見 interface 檔頭 + spec §13.3)。
 *
 * `/api/projects/*` 非 GROUP_HEADER_EXEMPT → 自動帶 `X-Current-Group`;
 * POST/PATCH 的 CSRF 由 transport 處理。DTO 逐欄取值(wire snake → camel),
 * 未知欄位(如 `created_by_cuid` / `trace_url`)FE 沒有消費端,刻意不映射。
 */
import type { EditDoc, EditsService, RenderEditResult } from '../editsService';

import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

const PATH = {
  edits: (pid: string) => `/api/projects/${encodeURIComponent(pid)}/edits`,
  edit: (pid: string, eid: string) =>
    `/api/projects/${encodeURIComponent(pid)}/edits/${encodeURIComponent(eid)}`,
  render: (pid: string, eid: string) =>
    `/api/projects/${encodeURIComponent(pid)}/edits/${encodeURIComponent(eid)}/render`,
} as const;

interface EditDto {
  eid?: unknown;
  title?: unknown;
  edl?: unknown;
  version?: unknown;
  updated_at?: unknown;
}

function mapEditDto(dto: EditDto): EditDoc {
  return {
    eid: typeof dto.eid === 'string' ? dto.eid : '',
    title: typeof dto.title === 'string' ? dto.title : null,
    edl:
      typeof dto.edl === 'object' && dto.edl !== null ? (dto.edl as Record<string, unknown>) : {},
    version: typeof dto.version === 'number' ? dto.version : 1,
    updatedAt: typeof dto.updated_at === 'string' ? dto.updated_at : null,
  };
}

interface RenderDto {
  job_id?: unknown;
  estimated_credits?: unknown;
  status?: unknown;
  total_steps?: unknown;
  edit_id?: unknown;
  edit_version?: unknown;
}

function mapRenderDto(dto: RenderDto): RenderEditResult {
  return {
    jobId: typeof dto.job_id === 'string' ? dto.job_id : '',
    estimatedCredits: typeof dto.estimated_credits === 'number' ? dto.estimated_credits : 0,
    status: typeof dto.status === 'string' ? dto.status : '',
    totalSteps: typeof dto.total_steps === 'number' ? dto.total_steps : 0,
    editId: typeof dto.edit_id === 'string' ? dto.edit_id : '',
    editVersion: typeof dto.edit_version === 'number' ? dto.edit_version : 0,
  };
}

export function createHttpEditsService(config: HttpClientConfig): EditsService {
  return {
    async create(projectId, req) {
      const res = await httpRequestWithRefresh<EditDto>(config, 'POST', PATH.edits(projectId), {
        title: req.title ?? null,
        edl: req.edl,
      });
      return mapEditDto(unwrapResult(res, 'edits.create'));
    },
    async get(projectId, editId) {
      const res = await httpRequestWithRefresh<EditDto>(config, 'GET', PATH.edit(projectId, editId));
      return mapEditDto(unwrapResult(res, 'edits.get'));
    },
    async update(projectId, editId, req) {
      const body: Record<string, unknown> = { expected_version: req.expectedVersion };
      if (req.title !== undefined) body.title = req.title;
      if (req.edl !== undefined) body.edl = req.edl;
      const res = await httpRequestWithRefresh<EditDto>(
        config,
        'PATCH',
        PATH.edit(projectId, editId),
        body,
      );
      return mapEditDto(unwrapResult(res, 'edits.update'));
    },
    async render(projectId, editId) {
      // 空 body:端點吃「已存的當前版本」(spec §13.3)——內容在 render 前一步 PATCH 上去。
      const res = await httpRequestWithRefresh<RenderDto>(
        config,
        'POST',
        PATH.render(projectId, editId),
      );
      return mapRenderDto(unwrapResult(res, 'edits.render'));
    },
  };
}
